import CallIcon from "@mui/icons-material/Call";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PortraitSharpIcon from "@mui/icons-material/PortraitSharp";
import dayjs from "dayjs";
import * as Yup from "yup";
import { Task } from "../../../../services";
// import FlagIcon from "@mui/icons-material/Flag";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import PeopleIcon from "@mui/icons-material/People";
// import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { green, orange, red } from "@mui/material/colors";
import { GridColDef } from "@mui/x-data-grid";

export type TaskFilters = {
  patientName: string | null;
  assign_to: string | null;
  assign_by: string | null;
  status: string | null;
  due_date: string | null;
  priority: string | null;
  action_type: string | null;
  searchString: string | null;
};

export const DEFAULT_FILTERS = {
  patientName: null,
  assign_to: null,
  assign_by: null,
  status: null,
  due_date: null,
  priority: null,
  action_type: null,
  searchString: null,
};

export const TABS = [
  {
    label: "Patient Centric Tasks",
    value: "patient-centric-tasks",
  },
  {
    label: "Provider Centric Tasks",
    value: "provider-centric-tasks",
  },
];

export const INITIAL_EXPANDED = Object.entries(DEFAULT_FILTERS).reduce(
  (acc, [k]) => {
    acc[k] = false;
    return acc;
  },
  {} as Record<string, boolean>
);

export const FILTER_KEYS = [
  "assign_to",
  "assign_by",
  "due_date",
  "priority",
  "action_type",
];
export const TASK_PRIORITIES = [
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

export const TASK_ACTIONS = [
  { value: "CALL", label: "Call" },
  { value: "EMAIL", label: "Email" },
  { value: "MESSAGE", label: "Message" },
  { value: "OTHER", label: "Other" },
  { value: "VISIT", label: "Visit" },
];

const TASK_END_TYPE = {
  ON_DATE: "on_date",
  AFTER_N_EVENTS: "after_n_events",
} as const;

export const TASK_END_DATE_BEFORE_DUE_DATE_ERROR =
  "End date cannot be before the due date";

export const TASK_INITIAL_VALIDATION_SCHEMA = Yup.object({
  title: Yup.object().required().label("Title"),
  priority: Yup.string().required().label("Priority"),
  patient: Yup.object().when("providerTask", {
    is: true,
    // then: (schema) => schema.notRequired(),
    then: (schema) => schema.notRequired().label("Patient"),
    otherwise: (schema) => schema.required().label("Patient"),
  }),
  actions: Yup.object().when("patient", {
    is: (patient: unknown) => !!patient,
    then: (schema) => schema.required().label("Action"),
    otherwise: (schema) => schema.notRequired().label("Action"),
  }),
  dueDate: Yup.string().required().label("Due Date/Time"),
  assignTo: Yup.object().when(["providerTask"], ([providerTask]) => {
    return providerTask
      ? Yup.object()
          .required("Please select a care team member to assign the task.")
          .label("Assignee")
      : Yup.object().notRequired();
  }),
  isRecurring: Yup.boolean().nullable().default(false),
  recurringFrequency: Yup.string().when("isRecurring", {
    is: true,
    then: (schema) => schema?.required().label("Recurring Frequency"),
    otherwise: (schema) => schema?.notRequired(),
  }),
  endType: Yup.string().when("isRecurring", {
    is: true,
    then: (schema) => schema?.required().label("End Type"),
    otherwise: (schema) => schema?.notRequired(),
  }),
  endDate: Yup.date().when(["isRecurring", "endType"], {
    is: (isRecurring: boolean, endType: string) =>
      isRecurring && endType === TASK_END_TYPE.ON_DATE,
    then: (schema) =>
      schema
        .required()
        .label("End Date")
        .test(
          "not-before-due-date",
          TASK_END_DATE_BEFORE_DUE_DATE_ERROR,
          (value, context) =>
            !value ||
            !context.parent.dueDate ||
            !dayjs(value).isBefore(dayjs(context.parent.dueDate), "day")
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  endAfter: Yup.number().when(["isRecurring", "endType"], {
    is: (isRecurring: boolean, endType: string) =>
      isRecurring && endType === TASK_END_TYPE.AFTER_N_EVENTS,
    then: (schema) => schema.required().min(1).label("End After"),
    otherwise: (schema) => schema.notRequired(),
  }),
  repeatEvery: Yup.number().when(["isRecurring", "recurringFrequency"], {
    is: (isRecurring: boolean, recurringFrequency: string) =>
      isRecurring && recurringFrequency === "CUSTOM",
    then: (schema) => schema.required().min(1).label("Repeat Every"),
    otherwise: (schema) => schema.notRequired(),
  }),
  repeatOn: Yup.string().when(["isRecurring", "recurringFrequency"], {
    is: (isRecurring: boolean, recurringFrequency: string) =>
      isRecurring && recurringFrequency === "CUSTOM",
    then: (schema) => schema.required().label("Repeat After"),
    otherwise: (schema) => schema.notRequired(),
  }),
  reminder_set: Yup.boolean().nullable().default(false),
  reminder_mediums: Yup.array().when("reminder_set", {
    is: true,
    then: (schema) => schema?.required().min(1).label("Remind Medium"),
    otherwise: (schema) => schema?.notRequired(),
  }),
  reminder_before_number: Yup.number().when("reminder_set", {
    is: true,
    then: (schema) => schema?.required().label("Remind Before"),
    otherwise: (schema) => schema?.notRequired(),
  }),
  reminder_before_unit: Yup.string().when("reminder_set", {
    is: true,
    then: (schema) => schema?.required().label("Remind Before Unit"),
    otherwise: (schema) => schema?.notRequired(),
  }),
});

export const TASK_INITIAL_VALUES: any = {
  providerTask: false,
  priority: "MEDIUM",
  isRecurring: false,
  title: undefined,
  description: "",
  actions: null,
  assignTo: null,
  dueDate: null,
  dueTime: null,
  isScheduled: false,
  recordedDate: null,
  recordedTime: null,
  reminderMedium: "EMAIL",
  // recurringFrequency: "daily",
  // reminder_set: false,

  // endType: "never",
  // endDate: new Date(),
  // endAfter: 5,

  // customFrequency: 1,
  // customFrequencyUnit: "days",
  // selectedWeekdays: [],
  reminder_set: false,
  reminder_mediums: [],
  reminder_before_number: undefined,
  reminder_before_unit: undefined,
};

export type TaskFormik =
  | (Omit<
      Task,
      | "isRecurring"
      | "reminder_set"
      | "reminder_mediums"
      | "reminder_before_number"
      | "reminder_before_unit"
      | "isScheduled"
      | "recordedDate"
      | "recordedTime"
      | "reminderMedium"
    > & {
      isRecurring?: boolean;
      reminder_set?: boolean;
      reminder_mediums?: string[];
      reminder_before_number?: number;
      reminder_before_unit?: string;
      isScheduled?: boolean;
      recordedDate?: string;
      recordedTime?: string;
      reminderMedium?: string;
    })
  | Record<string, any>;

export const FLAG_MAPPING = {
  NONE: { icon: null, color: null },
  RED: { icon: ErrorOutlineIcon, color: red[500] },
  GREEN: { icon: ThumbUpOutlinedIcon, color: green[500] },
  ORANGE: { icon: ThumbUpOutlinedIcon, color: orange[500] },
};

export const ACTIVITY_MAPPING = {
  Visit: { icon: PeopleIcon, color: "#104E84" },
  Other: { icon: PortraitSharpIcon, color: "#104E84" },
  Message: { icon: MessageOutlinedIcon, color: "#104E84" },
  Email: { icon: EmailOutlinedIcon, color: "#104E84" },
  Call: { icon: CallIcon, color: "#104E84" },
};

export const dynamicColumns = (
  COLUMNS: GridColDef[],
  patientData: boolean,
  isProviderTask: boolean,
  todoTaskFilter: any
): GridColDef[] => {
  const REVIEW_FIELDS = ["reviewedStatus", "reviewedBy", "reviewedDate"];
  // ECRH-5030: Patient-Centric grid hides these columns. Definitions are kept
  // in Tasks.tsx so the Provider-Centric tab keeps working and the workflow
  // is discoverable if the spec is reverted.
  const PATIENT_CENTRIC_HIDDEN = [
    "resolvedBy",
    "reviewedStatus",
    "description",
    "flagStatus",
    "createdDate",
  ];
  if (isProviderTask) {
    if (todoTaskFilter.toDoTask) {
      return COLUMNS?.filter(
        (col) =>
          ![
            "resolvedDate",
            "resolvedBy",
            "createdDate",
            ...REVIEW_FIELDS,
          ].includes(col.field)
      ) as GridColDef[];
    } else {
      return COLUMNS?.filter(
        (col) => !["activity", "Action", ...REVIEW_FIELDS].includes(col.field)
      ) as GridColDef[];
    }
  } else {
    return COLUMNS?.filter(
      (col) =>
        !PATIENT_CENTRIC_HIDDEN.includes(col.field) &&
        (patientData ? col.field !== "name" : true)
    ) as GridColDef[];
  }
};
